import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Loader2 } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { fill } from "@/lib/i18n/fill";
import type { Language, Translations } from "@/lib/i18n/types";
import en from "@/lib/i18n/translations/en";
import et from "@/lib/i18n/translations/et";
import { useIssuesStore } from "@/stores/pm/hooks/usePMStore";
import { IssueDetailContent } from "@/domains/pm/components/issues/IssueDetailPanel";
import { applyPMRangeSelect } from "@/domains/pm/utils/rangeSelect";
import type { ViewerDocumentData } from "@/domains/documents/hooks/useDocumentViewer";
import {
  buildProtocolDocument,
  filterProtocolData,
  formatProtocolRange,
  protocolFilename,
  protocolDataKey,
  protocolQueryBounds,
  protocolColumns,
  protocolRangeForPreset,
  protocolSections,
  matchesProtocolFilter,
  EMPTY_PROTOCOL_FILTER,
  hasProtocolContent,
  incompleteProtocolNotes,
  sortProtocolIssues,
  defaultProtocolDocumentDate,
  type ProtocolDateRange,
  type ProtocolDocumentDate,
  type ProtocolPreset,
  type ProtocolNote,
  type ProtocolSectionId,
  type ProtocolSectionSelection,
  type ProtocolColumnId,
  type ProtocolColumnSelection,
  type ProtocolIssueFilter,
} from "@/domains/pm/utils/protocol";
import { sortIssues, type IssueSort, type IssueSortField } from "@/domains/pm/utils/groupAndSort";
import {
  downloadProtocolPdf,
  renderProtocolPdf,
  type ProtocolRenderStats,
} from "@/domains/pm/utils/protocolPdf";
import {
  readStoredProtocolLayout,
  storeProtocolLayout,
  type ProtocolLayout,
} from "@/domains/pm/utils/protocolLayout";
import {
  planProtocolAttachments,
  protocolAttachmentFilename,
  protocolAttachmentIdentity,
  type ProtocolAttachmentContent,
  type ProtocolOriginal,
} from "@/domains/pm/utils/protocolAttachments";
import {
  loadProtocolAttachments,
  loadProtocolOriginals,
} from "@/domains/pm/utils/protocolAttachmentLoader";
import type { ProtocolDocumentAttachments } from "@/domains/pm/utils/protocol";
import { ProtocolStepIndicator } from "@/domains/pm/components/issues/protocol/ProtocolStepIndicator";
import { ProtocolDateStep } from "@/domains/pm/components/issues/protocol/ProtocolDateStep";
import { ProtocolIssueStep } from "@/domains/pm/components/issues/protocol/ProtocolIssueStep";
import { ProtocolContentStep } from "@/domains/pm/components/issues/protocol/ProtocolContentStep";
import { useProtocolCount } from "@/domains/pm/components/issues/protocol/useProtocolCount";
import { useProtocolComments } from "@/domains/pm/components/issues/protocol/useProtocolComments";
import { ProtocolDialogActions } from "@/domains/pm/components/issues/protocol/ProtocolDialogActions";
import { ProtocolPreviewDialog } from "@/domains/pm/components/issues/protocol/ProtocolPreviewDialog";
import { useProtocolData } from "@/domains/pm/components/issues/protocol/useProtocolData";
import {
  useProtocolAttachments,
  useProtocolIssueAttachments,
} from "@/domains/pm/components/issues/protocol/useProtocolAttachments";
import { ProtocolAttachmentsSection } from "@/domains/pm/components/issues/protocol/ProtocolAttachmentsSection";
import {
  ProtocolIssueAttachmentsOption,
  type IssueAttachmentsSetting,
} from "@/domains/pm/components/issues/protocol/ProtocolIssueAttachmentsOption";
import type { ProtocolStep } from "@/domains/pm/components/issues/protocol/steps";
import type { ProtocolData } from "@/domains/pm/types";

interface ProtocolExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  buildingId: number;
  /** Null until the building detail has loaded; the document says "Unknown". */
  buildingName: string | null;
  buildingProjectId?: number;
  projectName?: string | null;
  /** The page's single document viewer. `IssueDetailContent` needs it or its
   *  document chips do nothing; a second viewer would be a second blob
   *  lifecycle competing for the same corner. */
  openViewer: (doc: ViewerDocumentData) => Promise<void>;
  /** Fires after the PDF has been downloaded, with the exact bytes that were
   *  written, so the page can offer to file the same document into a project. */
  onCreated?: (created: { file: File; filename: string }) => void;
}

type OpenDialogProps = Omit<ProtocolExportDialogProps, "open">;

/** The user's ticks, tagged with the loaded period they were made against. */
type ProtocolSelection = { key: string; ids: Set<number> };

function useProtocolForm(language: Language) {
  const [step, setStep] = useState<ProtocolStep>("dates");
  const [preset, setPreset] = useState<ProtocolPreset>("today");
  const [range, setRange] = useState<ProtocolDateRange>(() => protocolRangeForPreset("today"));
  const [customRange, setCustomRange] = useState<DateRange>();
  // Everything on but the activity log. The step's point is that the contents
  // are visible and ticked, so there is no hidden choice to force — and a step
  // that opens with every box empty reads as broken rather than as a question.
  // The log is the exception: it is the longest section by far and the one
  // most protocols are filed without, so it is opted into.
  const [sections, setSections] = useState<ProtocolSectionSelection>(() =>
    protocolSections({ activity: false })
  );
  const [notes, setNotes] = useState<ProtocolNote[]>([]);
  const [columns, setColumns] = useState<ProtocolColumnSelection>(() => protocolColumns());

  const toggleColumn = (id: ProtocolColumnId, checked: boolean) =>
    setColumns((current) => ({ ...current, [id]: checked }));

  const toggleSection = (id: ProtocolSectionId, checked: boolean) =>
    setSections((current) => ({ ...current, [id]: checked }));
  // The document's own language, defaulting to the interface's but free to
  // diverge — a protocol is filed and circulated.
  const [pdfLanguage, setPdfLanguage] = useState<Language>(language);
  // Prefilled with now, the usual answer; the author can move or clear it.
  const [documentDate, setDocumentDate] = useState<ProtocolDocumentDate>(() =>
    defaultProtocolDocumentDate()
  );
  // The look, remembered per browser: a house style is chosen once.
  const [layout, setLayoutState] = useState<ProtocolLayout>(readStoredProtocolLayout);
  const setLayout = (value: ProtocolLayout) => {
    setLayoutState(value);
    storeProtocolLayout(value);
  };

  const selectPreset = (value: ProtocolPreset) => {
    setPreset(value);
    if (value === "custom") {
      setCustomRange(undefined);
      return;
    }
    setRange(protocolRangeForPreset(value));
  };

  const selectCustomRange = (selected: DateRange | undefined) => {
    setCustomRange(selected);
    if (selected?.from && selected.to) setRange({ from: selected.from, to: selected.to });
  };

  const hasCompleteRange = preset !== "custom" || Boolean(customRange?.from && customRange.to);
  return {
    step,
    setStep,
    preset,
    range,
    customRange,
    sections,
    notes,
    toggleSection,
    setNotes,
    columns,
    toggleColumn,
    pdfLanguage,
    setPdfLanguage,
    documentDate,
    setDocumentDate,
    layout,
    setLayout,
    selectPreset,
    selectCustomRange,
    hasCompleteRange,
    rangeLabel: formatProtocolRange(range, language),
  };
}

const OpenProtocolDialog = observer(function OpenProtocolDialog(props: OpenDialogProps) {
  const { t, language } = useLanguage();
  const copy = t.pm.issues.protocol;
  const form = useProtocolForm(language);
  const data = useProtocolData({
    buildingId: props.buildingId,
    buildingProjectId: props.buildingProjectId,
  });
  const comments = useProtocolComments();
  const periodCount = useProtocolCount({
    buildingId: props.buildingId,
    buildingProjectId: props.buildingProjectId,
  });

  const [exporting, setExporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preparingAttachments, setPreparingAttachments] = useState(false);
  const attachments = useProtocolAttachments();
  // Off until asked for: it costs a read, and thumbnails lengthen the table.
  const [issueAttachmentSetting, setIssueAttachmentSetting] = useState<IssueAttachmentsSetting>({
    enabled: false,
    inTable: true,
    inAppendix: false,
  });
  // Read bytes, by file identity, so Preview then Export reads each file once.
  const attachmentCacheRef = useRef(new Map<string, ProtocolAttachmentContent>());
  // Each file's own bytes, carried inside the exported PDF behind a paperclip.
  const [embedOriginals, setEmbedOriginals] = useState(true);
  const originalsCacheRef = useRef(new Map<string, ProtocolOriginal>());
  // The blob is kept beside its URL so the preview's tab hatch can mint a
  // URL of its own — one this component never revokes.
  const [preview, setPreview] = useState<{ url: string; blob: Blob; filename: string } | null>(
    null
  );
  const [selection, setSelection] = useState<ProtocolSelection | null>(null);
  // The row a user drilled into, and the anchor a Shift-click ranges from.
  const [detailIssueId, setDetailIssueId] = useState<number | null>(null);
  const [anchorId, setAnchorId] = useState<number | null>(null);
  const [filter, setFilter] = useState<ProtocolIssueFilter>(EMPTY_PROTOCOL_FILTER);
  // null = the document's own order, so an untouched picker matches the PDF.
  const [sort, setSort] = useState<IssueSort | null>(null);
  const issuesStore = useIssuesStore();

  const previewAbortRef = useRef<AbortController | null>(null);
  const exportAbortRef = useRef<AbortController | null>(null);
  // The URL also lives in a ref so the unmount cleanup cannot close over a
  // stale value; state alone would leak the most recent blob.
  const previewUrlRef = useRef<string | null>(null);

  // Listed in the order the document prints them, so the preview never looks
  // like it reordered the user's selection.
  const issues = useMemo(
    () => sortProtocolIssues(data.state.data?.issues ?? []),
    [data.state.data]
  );

  // Everything is included until the user says otherwise, and the default is
  // derived from the loaded period's key — so changing the period clears the
  // ticks by itself, with no reset effect to forget. Taken as a function of a
  // selection rather than of state, so `setIncluded`'s updater can resolve the
  // same default against whatever selection React hands it.
  const includedIn = useCallback(
    (current: ProtocolSelection | null): Set<number> =>
      current && current.key === data.state.key
        ? current.ids
        : new Set(issues.map((issue) => issue.id)),
    [data.state.key, issues]
  );

  const included = useMemo<ReadonlySet<number>>(
    () => includedIn(selection),
    [includedIn, selection]
  );

  // Step 1 counts the period as soon as it is complete, so the user sees its
  // size before committing to it. `countProtocolIssues` is a slim scan of the
  // same membership filter the export uses, not the export's own fan-out.
  const countPeriod = periodCount.count;
  const periodKey = form.hasCompleteRange
    ? protocolDataKey(props.buildingId, form.range, props.buildingProjectId)
    : null;
  // `periodKey` is the range's identity; `form.range` is a fresh pair of Dates
  // on every render, so depending on it would re-count continuously. The ref
  // carries the value while the key decides when to read it.
  const rangeRef = useRef(form.range);
  rangeRef.current = form.range;
  useEffect(() => {
    if (periodKey) countPeriod(rangeRef.current);
  }, [periodKey, countPeriod]);

  const countState = periodCount.state;
  const countLabel = useMemo(() => {
    if (!periodKey || countState.key !== periodKey) return null;
    if (countState.status === "loading") return copy.issueCountLoading;
    if (countState.status !== "ready") return null;
    // Truncated means the cap was hit, so the number is a floor: say "at
    // least N" rather than asserting a total the scan never reached.
    if (countState.truncated)
      return fill(copy.issueCountAtLeast, { count: String(countState.count) });
    return countState.count === 1
      ? copy.issueCountOne
      : fill(copy.issueCountMany, { count: String(countState.count) });
  }, [periodKey, countState, copy]);

  // Fetched the moment the column is ticked rather than at Export, so the wait
  // usually happens while the user is still reading the step. An effect, not
  // the toggle handler, so Back → Continue and a re-tick take the same path.
  //
  // Gated on the issue table as well as the column, because the column's
  // status line and its Retry button live INSIDE the table's block: with the
  // table off, a failed fetch would block Export with no way to recover.
  const commentsWanted = form.sections.issues && form.columns.commentCount;
  const loadComments = comments.load;
  // The TICKED issues, not the period's: the fetch has a 5000-comment cap spent
  // in issue order, so asking for issues the user has dropped lets them eat the
  // budget and cut comments off the ones being exported. Sorted so that
  // unticking and re-ticking back to the same set is the same request.
  const selectedIssueIds = useMemo(() => Array.from(included).sort((a, b) => a - b), [included]);
  // The key of the period actually LOADED, as distinct from `periodKey` above,
  // which is the range the user has currently chosen on step 1.
  const loadedKey = data.state.key;
  useEffect(() => {
    if (form.step !== "content" || !commentsWanted) return;
    if (!loadedKey || data.state.status !== "ready") return;
    // The comment query is bounded by the same range as the period fetch, so it
    // may only run while step 1's range still IS the loaded period — Back plus
    // an edited range would otherwise bound the comments by a period the issues
    // in hand do not come from.
    if (periodKey !== loadedKey) return;
    loadComments(loadedKey, selectedIssueIds, protocolQueryBounds(rangeRef.current));
  }, [
    form.step,
    commentsWanted,
    periodKey,
    loadedKey,
    data.state.status,
    selectedIssueIds,
    loadComments,
  ]);

  const commentsPending =
    commentsWanted && (comments.state.key !== loadedKey || comments.state.status !== "ready");

  // The issues the document lists, in its order — what the issue attachment
  // groups follow.
  const includedIssues = useMemo(
    () => issues.filter((issue) => included.has(issue.id)),
    [issues, included]
  );
  const periodIssueIds = useMemo(() => issues.map((issue) => issue.id), [issues]);
  // A sub-option of the issue table: with the table off it means nothing.
  const includeIssueAttachments = form.sections.issues && issueAttachmentSetting.enabled;
  const issueAttachments = useProtocolIssueAttachments(
    periodIssueIds,
    form.step === "content" && includeIssueAttachments && data.state.status === "ready"
  );
  const issueAttachmentCount = useMemo(
    () =>
      includedIssues.reduce(
        (sum, issue) => sum + (issueAttachments.byIssue.get(issue.id)?.length ?? 0),
        0
      ),
    [includedIssues, issueAttachments.byIssue]
  );
  const attachmentPlan = useMemo(
    () =>
      planProtocolAttachments({
        added: attachments.added,
        issueAttachments: includeIssueAttachments ? issueAttachments.byIssue : null,
        placement: issueAttachmentSetting,
        issues: includedIssues,
      }),
    [
      attachments.added,
      includeIssueAttachments,
      issueAttachments.byIssue,
      issueAttachmentSetting,
      includedIssues,
    ]
  );
  // Blocked rather than exported without them, as comments are: a protocol
  // the author asked to carry the issues' files must not quietly carry none.
  const issueAttachmentsPending = includeIssueAttachments && issueAttachments.status !== "ready";

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
  }, []);

  // Depends on `data.abort` (a stable useCallback), never on the hook's return
  // object — that is a fresh literal every render, and an unstable `abortAll`
  // makes the teardown effect below re-run on each render, aborting the very
  // fetch it is meant to outlive.
  const abortData = data.abort;
  const abortCount = periodCount.abort;
  const abortComments = comments.abort;
  const abortAll = useCallback(() => {
    abortData();
    abortCount();
    abortComments();
    previewAbortRef.current?.abort();
    exportAbortRef.current?.abort();
  }, [abortData, abortCount, abortComments]);

  // Fires on close too — the wrapper unmounts this component when `open` goes
  // false, so this is the single exit every teardown funnels through.
  useEffect(
    () => () => {
      abortAll();
      revokePreviewUrl();
    },
    [abortAll, revokePreviewUrl]
  );

  // The updater form, not a value: two toggles batched into one render must
  // each see the previous one's result, where a value computed from the
  // `included` this render closed over would drop all but the last.
  const setIncluded = (update: (current: ReadonlySet<number>) => Set<number>) => {
    const key = data.state.key;
    if (!key) return;
    setSelection((current) => ({ key, ids: update(includedIn(current)) }));
  };

  // What the user can actually see. Filtering and sorting live here, not in
  // the step, because Select all and the Shift-range basis below must agree
  // with the rows on screen — a step that filtered privately would tick rows
  // nobody could see and range across hidden ones.
  const visibleIssues = useMemo(() => {
    const narrowed = issues.filter((issue) => matchesProtocolFilter(issue, filter));
    return sort ? sortIssues(narrowed, sort, language) : narrowed;
  }, [issues, filter, sort, language]);

  const orderedIds = useMemo(() => visibleIssues.map((issue) => issue.id), [visibleIssues]);

  const toggleIssue = (id: number, shiftKey: boolean) => {
    // Clearing a stray text selection is a DOM effect, so it stays here rather
    // than in the pure helper.
    if (shiftKey && anchorId != null) window.getSelection?.()?.removeAllRanges();
    setIncluded(
      (current) =>
        applyPMRangeSelect({
          selected: current,
          orderedIds,
          clickedId: id,
          anchorId,
          shiftKey,
          // The picker starts with everything ticked, so a union-mode range
          // would be a no-op on the common gesture ("untick this whole run").
          mode: "anchor",
        }).selected
    );
    // Outside the updater, which must stay pure: the helper always re-anchors
    // on the clicked row, Shift included.
    setAnchorId(id);
  };

  /**
   * "Select all" means the rows in front of the user.
   *
   * Rows a filter hides keep whatever tick they had — a filter narrows the
   * view, it does not edit the selection. Clearing them would silently drop
   * issues from a filed record that nobody chose to drop.
   */
  const toggleAll = () => {
    setIncluded((current) => {
      const next = new Set(current);
      const allVisibleTicked = orderedIds.length > 0 && orderedIds.every((id) => next.has(id));
      for (const id of orderedIds) {
        if (allVisibleTicked) next.delete(id);
        else next.add(id);
      }
      return next;
    });
    // A Shift-click straight after Select all must not range from a row the
    // user last touched several gestures ago.
    setAnchorId(null);
  };

  const openIssueDetail = (id: number) => {
    setDetailIssueId(id);
    void issuesStore.selectIssue(id);
  };

  const closeIssueDetail = () => {
    // The store holds the edited row; copy it into the period before the
    // selection is dropped, or the picker keeps showing the pre-edit values
    // and the PDF prints them.
    const edited = issuesStore.selectedIssue;
    if (edited && edited.id === detailIssueId) data.patchIssue({ ...edited });
    issuesStore.clearSelection();
    setDetailIssueId(null);
  };

  /**
   * The attached files' bytes, read for this render. Null when nothing is
   * attached. A file that cannot be read comes back as "failed" and is listed
   * as such — only a cancel rejects.
   */
  /**
   * The files' printable bytes and, for an export that wants them, their
   * originals. A preview never carries originals: its viewer shows no
   * attachments, and fetching every file at full size would only slow it.
   */
  const addedIdentities = useMemo(
    () => new Set(attachments.added.map(protocolAttachmentIdentity)),
    [attachments.added]
  );

  const loadAttachmentsFor = async (
    signal: AbortSignal,
    withOriginals: boolean
  ): Promise<{ attachments: ProtocolDocumentAttachments | null; originalsLeftOut: number }> => {
    const plan = attachmentPlan;
    if (plan.files.length === 0) return { attachments: null, originalsLeftOut: 0 };
    setPreparingAttachments(true);
    try {
      const contents = await loadProtocolAttachments(
        plan.files,
        attachmentCacheRef.current,
        signal
      );
      const { originals, leftOut } = withOriginals
        ? await loadProtocolOriginals(
            // Only the files attached here: the issues' own already live in
            // Franklin, a click away from anyone reading the protocol.
            plan.files.filter((file) => addedIdentities.has(file.identity)),
            contents,
            originalsCacheRef.current,
            signal
          )
        : { originals: new Map<string, ProtocolOriginal>(), leftOut: 0 };
      return {
        attachments: {
          files: plan.files.map((file) => ({
            number: file.number,
            filename: protocolAttachmentFilename(file.attachment),
            content: contents.get(file.identity) ?? { type: "failed" },
            original: originals.get(file.identity) ?? null,
          })),
          groups: plan.groups.map(({ issueId, heading, numbers }) => ({
            issueId,
            heading,
            numbers,
          })),
          table: plan.table,
        },
        originalsLeftOut: leftOut,
      };
    } finally {
      setPreparingAttachments(false);
    }
  };

  /** The document as the step describes it, built from the period already in
   *  hand and in the language chosen for the PDF — not the interface's. */
  const modelFor = (
    loaded: ProtocolData,
    documentAttachments: ProtocolDocumentAttachments | null
  ) => {
    const bundle: Translations = form.pdfLanguage === "et" ? et : en;
    // Merged BEFORE filtering, so `filterProtocolData` stays the one place the
    // selection narrows the document.
    const withComments: ProtocolData =
      commentsWanted && comments.state.comments
        ? {
            ...loaded,
            comments: comments.state.comments,
            commentsTruncated: comments.state.truncated,
            commentsIncomplete: comments.state.incomplete,
          }
        : loaded;
    return buildProtocolDocument({
      data: filterProtocolData(withComments, included),
      range: form.range,
      sections: form.sections,
      columns: form.columns,
      notes: form.notes,
      buildingName: props.buildingName,
      buildingProjectId: props.buildingProjectId,
      projectName: props.projectName,
      // The unfiltered period, so the document can own up to being a subset of
      // it. Read from `loaded` rather than the filtered copy, which by then
      // has already lost the issues the picker dropped.
      availableIssueCount: loaded.issues.length,
      language: form.pdfLanguage,
      translations: bundle,
      documentDate: form.documentDate,
      layout: form.layout,
      attachments: documentAttachments,
    });
  };

  const openPreview = async () => {
    const loaded = data.state.data;
    if (!loaded) return;
    // A second click supersedes the first rather than racing it.
    previewAbortRef.current?.abort();
    const controller = new AbortController();
    previewAbortRef.current = controller;
    setPreviewing(true);
    try {
      const { attachments: documentAttachments } = await loadAttachmentsFor(
        controller.signal,
        false
      );
      const bytes = await renderProtocolPdf(
        modelFor(loaded, documentAttachments),
        controller.signal
      );
      // Nothing has been created yet, so an abort here leaks nothing.
      if (controller.signal.aborted) return;
      revokePreviewUrl();
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      // Ref assigned synchronously with the create, so cleanup can always find it.
      previewUrlRef.current = url;
      setPreview({ url, blob, filename: protocolFilename(form.range) });
    } catch (error) {
      if (controller.signal.aborted) return;
      console.error("Protocol preview failed", error);
      toast.error(copy.previewError);
    } finally {
      // Only the render that still owns the preview clears the spinner: a
      // superseded one settling later would report done while a newer render is
      // still working. The Preview button is disabled while previewing, so this
      // is defence in depth against a second entry point rather than a live
      // path. A cancel leaves the ref pointing at this controller, so the
      // spinner still clears there instead of sticking on.
      if (previewAbortRef.current === controller) setPreviewing(false);
    }
  };

  const closePreview = () => {
    previewAbortRef.current?.abort();
    revokePreviewUrl();
    setPreview(null);
  };

  const exportDocument = async () => {
    const loaded = data.state.data;
    if (!loaded) return;
    const controller = new AbortController();
    exportAbortRef.current = controller;
    setExporting(true);
    try {
      const filename = protocolFilename(form.range);
      // The period is already in hand — step 3 is only reachable once it has
      // loaded — so the only reads here are the attached files' bytes.
      const { attachments: documentAttachments, originalsLeftOut } = await loadAttachmentsFor(
        controller.signal,
        embedOriginals
      );
      const stats: ProtocolRenderStats = { attachmentsNotIncluded: 0 };
      const bytes = await downloadProtocolPdf(
        modelFor(loaded, documentAttachments),
        filename,
        controller.signal,
        stats
      );
      if (!bytes || controller.signal.aborted) return;
      // The index says which; this says that there were any, before the
      // protocol is circulated on the assumption it carries everything.
      if (stats.attachmentsNotIncluded > 0) {
        toast.warning(
          fill(copy.attachments.notIncluded, { count: String(stats.attachmentsNotIncluded) })
        );
      }
      if (originalsLeftOut > 0) {
        toast.warning(fill(copy.attachments.originalsLeftOut, { count: String(originalsLeftOut) }));
      }
      // The card reports the success and offers to file it, so a toast saying
      // the same thing would stack on top of it in the same corner.
      props.onCreated?.({
        file: new File([bytes], filename, { type: "application/pdf" }),
        filename,
      });
      props.onOpenChange(false);
    } catch (error) {
      // A cancelled export is not a failure: the dialog closed the moment
      // Cancel was pressed.
      if (controller.signal.aborted) return;
      console.error("Protocol export failed", error);
      toast.error(copy.exportError);
    } finally {
      setExporting(false);
    }
  };

  const goContinue = () => {
    if (form.step === "dates") {
      data.load(form.range);
      form.setStep("issues");
      return;
    }
    form.setStep("content");
  };

  // Why Export is unavailable, said out loud: a disabled button fires no
  // pointer events, so a tooltip could not open, and `title` is invisible on
  // touch. Half-filled notes block rather than being dropped — silently
  // discarding text somebody typed is the worst outcome available here.
  const unfinishedNotes = incompleteProtocolNotes(form.notes);
  const hasContent = hasProtocolContent(form.sections, form.notes, attachmentPlan.files.length);
  const canExport =
    hasContent && unfinishedNotes.length === 0 && !commentsPending && !issueAttachmentsPending;
  const exportBlockedReason =
    unfinishedNotes.length > 0
      ? copy.noteIncomplete
      : !hasContent
        ? copy.nothingSelected
        : // Blocked rather than exported empty: a protocol printing "no
          // comments" for every issue because a fetch failed is exactly the
          // false record this document's design guards against.
          comments.state.status === "error" && commentsWanted
          ? copy.commentsError
          : commentsPending
            ? copy.commentsLoading
            : issueAttachmentsPending
              ? issueAttachments.status === "error"
                ? copy.attachments.issuesError
                : copy.attachments.issuesLoading
              : undefined;

  const canContinue =
    form.step === "dates"
      ? form.hasCompleteRange
      : // A period with nothing in it still exports, exactly as it does today;
        // only a period whose every issue was unticked is a dead end.
        data.state.status === "ready" && (issues.length === 0 || included.size > 0);

  return (
    <>
      <Dialog
        open
        onOpenChange={(next) => {
          if (!next) abortAll();
          props.onOpenChange(next);
        }}
      >
        {/* overflow-x-hidden is load-bearing, not tidying: `overflow-y-auto` makes
            overflow-x compute to `auto` too, and the vertical scrollbar it adds
            shrinks the content box AFTER the grid tracks were sized — so the
            content ends up exactly one scrollbar too wide and the dialog grows a
            horizontal scrollbar. Widening the dialog only moves that threshold. */}
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>
          <ProtocolStepIndicator step={form.step} copy={copy} />
          {form.step === "dates" && (
            <ProtocolDateStep
              preset={form.preset}
              customRange={form.customRange}
              hasCompleteRange={form.hasCompleteRange}
              rangeLabel={form.rangeLabel}
              countLabel={countLabel}
              language={language}
              copy={copy}
              onPresetSelect={form.selectPreset}
              onCustomRangeSelect={form.selectCustomRange}
            />
          )}
          {form.step === "issues" && (
            <ProtocolIssueStep
              status={data.state.status}
              issues={visibleIssues}
              allIssues={issues}
              filter={filter}
              sort={sort}
              onFilterChange={setFilter}
              onSortChange={(field: IssueSortField) =>
                setSort((prev) =>
                  prev?.field === field
                    ? { field, direction: prev.direction === "asc" ? "desc" : "asc" }
                    : { field, direction: "asc" }
                )
              }
              included={included}
              language={language}
              copy={copy}
              onToggle={toggleIssue}
              onToggleAll={toggleAll}
              onOpenIssue={openIssueDetail}
              onRetry={() => data.load(form.range)}
            />
          )}
          {form.step === "content" && (
            <ProtocolContentStep
              sections={form.sections}
              columns={form.columns}
              commentsStatus={commentsWanted ? comments.state.status : "idle"}
              onColumnToggle={form.toggleColumn}
              onRetryComments={() => {
                if (loadedKey && periodKey === loadedKey) {
                  loadComments(loadedKey, selectedIssueIds, protocolQueryBounds(rangeRef.current));
                }
              }}
              notes={form.notes}
              documentDate={form.documentDate}
              onDocumentDateChange={form.setDocumentDate}
              language={language}
              copy={copy}
              onSectionToggle={form.toggleSection}
              onNotesChange={form.setNotes}
              attachments={
                <ProtocolAttachmentsSection
                  buildingId={props.buildingId}
                  buildingProjectId={props.buildingProjectId}
                  openViewer={props.openViewer}
                  copy={copy}
                  added={attachments.added}
                  resolving={attachments.resolving}
                  onAddFiles={attachments.addFiles}
                  onAddDocuments={(docs) =>
                    void attachments.addDocuments(docs, copy.attachments.lookupError)
                  }
                  onRemove={attachments.remove}
                  // Offered only for files attached here — see loadAttachmentsFor.
                  embedOriginals={
                    attachments.added.length > 0
                      ? { checked: embedOriginals, onChange: setEmbedOriginals }
                      : null
                  }
                />
              }
              issueAttachmentsOption={
                <ProtocolIssueAttachmentsOption
                  value={issueAttachmentSetting}
                  onChange={setIssueAttachmentSetting}
                  status={issueAttachments.status}
                  fileCount={issueAttachmentCount}
                  onRetry={issueAttachments.retry}
                  copy={copy}
                />
              }
            />
          )}
          <ProtocolDialogActions
            step={form.step}
            canContinue={canContinue}
            canExport={canExport}
            blockedReason={exportBlockedReason}
            exporting={exporting}
            previewing={previewing}
            preparingAttachments={preparingAttachments}
            copy={copy}
            pdfLanguage={form.pdfLanguage}
            onPdfLanguageChange={form.setPdfLanguage}
            layout={form.layout}
            onLayoutChange={form.setLayout}
            onBack={() => form.setStep(form.step === "content" ? "issues" : "dates")}
            onContinue={goContinue}
            onPreview={() => void openPreview()}
            onExport={() => void exportDocument()}
          />
        </DialogContent>
      </Dialog>
      <ProtocolPreviewDialog preview={preview} title={copy.previewTitle} onClose={closePreview} />
      {/* The full issue, so the user can look before deciding. Same pattern as
          the 3D viewer's: IssueDetailContent (never IssueDetailPanel, which is
          a fixed aside with its own Escape listener), rendered only once the
          store has hydrated the row this dialog asked for. */}
      <Dialog
        open={detailIssueId !== null}
        onOpenChange={(next) => {
          if (!next) closeIssueDetail();
        }}
      >
        {/* Deliberately NOT raised above z-50. Every editable control inside —
            the assignee, status, discipline and due-date pickers, the ⋯ menu,
            the tooltips — is a Radix layer that portals to the body at z-50, a
            sibling of this panel rather than a descendant. Raising the panel to
            z-[60] therefore paints every one of those pickers BEHIND it. At the
            shared z-50 the stack falls out of portal order instead: the wizard
            mounts first, this panel second, a picker third. */}
        <DialogContent
          data-issue-panel
          hideCloseButton
          className="max-h-[85vh] max-w-3xl overflow-y-auto p-0"
        >
          <DialogTitle className="sr-only">{copy.issueDetailTitle}</DialogTitle>
          {issuesStore.selectedIssue && issuesStore.selectedIssue.id === detailIssueId ? (
            <IssueDetailContent
              issue={issuesStore.selectedIssue}
              onClose={closeIssueDetail}
              openViewer={props.openViewer}
            />
          ) : (
            <div className="flex h-48 items-center justify-center">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
});

export function ProtocolExportDialog({ open, ...props }: ProtocolExportDialogProps) {
  if (!open) return null;
  return <OpenProtocolDialog {...props} />;
}
